import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Download, Check, X } from "lucide-react";

export default function AdminTrialSlotBookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewShot, setViewShot] = useState(null);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/trial-slot-bookings"); setItems(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (b) => {
    try { await api.put(`/admin/trial-slot-bookings/${b.id}/approve`); toast.success("Booking approved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  const del = async (b) => {
    if (!window.confirm(`Remove booking for ${b.name} (${b.mobile})?\n\nThis will free up the mobile number for a new booking.`)) return;
    try { await api.delete(`/admin/trial-slot-bookings/${b.id}`); toast.success("Booking removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const exportCsv = () => {
    const rows = [["Name", "Mobile", "Date", "Time Slot", "Tehsil", "Playing Role", "Status", "Booked On"]];
    items.forEach((b) => rows.push([b.name, b.mobile, b.trial_date, b.time_slot || "", b.tehsil || "", b.role || "", b.status || "pending", new Date(b.created_at).toLocaleString()]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `NDPL-Trial-Bookings-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Trial Slot Bookings</div>
          <h1 className="font-display text-4xl mt-2">Trial Slot Bookings</h1>
        </div>
        <button onClick={exportCsv} data-testid="tsb-export" className="px-4 py-2 bg-[#39FF14] text-black text-xs uppercase tracking-widest font-bold flex items-center gap-2"><Download size={14}/>Download CSV</button>
      </div>
      <div className="mt-4 text-sm text-white/60">{items.length} booking{items.length !== 1 ? "s" : ""}</div>
      {loading ? (
        <div className="mt-6 text-white/50">Loading…</div>
      ) : (
        <div className="mt-6 overflow-x-auto border border-white/10 rounded" data-testid="admin-tsb-table">
          <table className="w-full text-sm">
            <thead className="bg-[#151F3D] text-white/70">
              <tr>{["Name", "Mobile", "Date", "Time Slot", "Screenshot", "Status", "Booked On", ""].map((h) => (
                <th key={h} className="px-3 py-3 text-left text-xs uppercase tracking-widest">{h}</th>))}</tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-white/40">No bookings yet.</td></tr>}
              {items.map((b) => (
                <tr key={b.id} className="border-t border-white/5" data-testid={`tsb-${b.id}`}>
                  <td className="px-3 py-3">{b.name}</td>
                  <td className="px-3 py-3">{b.mobile}</td>
                  <td className="px-3 py-3 text-[#FFD700]">{b.trial_date}</td>
                  <td className="px-3 py-3">{b.time_slot || "—"}</td>
                  <td className="px-3 py-3">
                    {b.screenshot_data_url ? (
                      <img src={b.screenshot_data_url} alt="screenshot" onClick={() => setViewShot(b)}
                        data-testid={`tsb-shot-${b.id}`} className="w-12 h-12 object-cover rounded border border-white/10 cursor-pointer hover:border-[#39FF14]"/>
                    ) : <span className="text-white/30 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-3">
                    <span data-testid={`tsb-status-${b.id}`} className={`text-xs px-2 py-1 border uppercase ${b.status === "approved" ? "border-[#39FF14] text-[#39FF14]" : "border-[#FFD700] text-[#FFD700]"}`}>
                      {b.status === "approved" ? "Approved" : "Pending"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-white/50">{new Date(b.created_at).toLocaleString()}</td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1">
                      {b.status !== "approved" && (
                        <button onClick={() => approve(b)} data-testid={`tsb-approve-${b.id}`} title="Approve booking" className="p-2 text-[#39FF14] hover:bg-[#39FF14]/20"><Check size={14}/></button>
                      )}
                      <button onClick={() => del(b)} data-testid={`tsb-del-${b.id}`} title="Remove booking" className="p-2 text-[#FF2442] hover:bg-[#FF2442]/20"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewShot && (
        <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-4" onClick={() => setViewShot(null)} data-testid="tsb-shot-modal">
          <div className="bg-[#0B132B] border border-white/15 max-w-lg w-full p-6 rounded-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl">{viewShot.name}</h2>
                <div className="text-xs text-white/60 mt-1">{viewShot.mobile} • {viewShot.trial_date} • {viewShot.time_slot}</div>
              </div>
              <button onClick={() => setViewShot(null)} className="text-white/60"><X /></button>
            </div>
            <div className="mt-4 text-xs uppercase tracking-widest text-white/60">Golden Ticket / Payment Screenshot</div>
            <img src={viewShot.screenshot_data_url} alt="screenshot" className="mt-2 w-full max-h-[60vh] object-contain bg-black/30 border border-white/10"/>
            <div className="mt-4 flex gap-2">
              {viewShot.status !== "approved" && (
                <button onClick={() => { approve(viewShot); setViewShot(null); }} data-testid="tsb-shot-approve" className="px-4 py-2 bg-[#39FF14] text-black uppercase text-xs tracking-widest font-bold flex items-center gap-2"><Check size={14}/>Approve</button>
              )}
              <button onClick={() => setViewShot(null)} className="px-4 py-2 border border-white/20 uppercase text-xs tracking-widest">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
